import { makeAutoObservable, runInAction } from 'mobx';
import UISettingsService, { IUISettings, CUSTOMER_COLUMN_USE, RETURNS_DATE_FIELD } from '../services/api/ui-settings-service';
import { TYPE, rootContainer } from '../services/container/root';
import { ISaveable } from './types';

interface IUISettingsChanges extends Partial<IUISettings> { }

class UISettingsStore implements ISaveable {
    private _model: IUISettings = {} as IUISettings;
    private _changes: IUISettingsChanges = {};
    private _isLoading: boolean = false;
    private _isSaving: boolean = false;

    constructor() {
        makeAutoObservable(this, {}, {autoBind: true});
    }

    private _setModel(uiSettings: IUISettings) {
        this._model = uiSettings;
        this._changes = {};
    }

    private get uiSettingsService(): UISettingsService {
        return rootContainer.get<UISettingsService>(TYPE.UISettingsService);
    }

    public get isLoading(): boolean {
        return this._isLoading;
    }

    public get returnsCustomerDisplayMode(): CUSTOMER_COLUMN_USE {
        return this._changes.returns_customer_display_mode ?? this._model.returns_customer_display_mode;
    }

    public get returnsDateField(): RETURNS_DATE_FIELD {
        return this._changes.returns_date_field ?? this._model.returns_date_field;
    }

    public setReturnsCustomerDisplayMode(value: CUSTOMER_COLUMN_USE) {
        this._changes.returns_customer_display_mode = value;
    }

    public setReturnsDateField(value: RETURNS_DATE_FIELD) {
        this._changes.returns_date_field = value;
    }

    public hasChanges() {
        return !!Object.keys(this._changes).length;
    }

    async load() {
        this._isLoading = true;
        try {
            const uiSettings = await this.uiSettingsService.fetchUISettings();
            runInAction(() => {
                this._isLoading = false;
                this._setModel(uiSettings);
            });
        } catch (error) {
            console.error(error);
            runInAction(() => {
                this._isLoading = false;
            });
        }
    }

    async save() {
        if (!this.hasChanges()) {
            return;
        }
        this._isSaving = true;
        try {
            const uiSettings = this.getUpdatedModel();
            const updatedUISettings = await this.uiSettingsService.saveUISettings(uiSettings);
            runInAction(() => {
                this._isSaving = false;
                this._setModel(updatedUISettings);
            });
        } catch (error) {
            console.error(error);
            runInAction(() => {
                this._isSaving = false;
            });
        }
    }

    private getUpdatedModel(): IUISettings {
        return { ...this._model, ...this._changes }
    }

    public isSaving(): boolean {
        return this._isSaving;
    }
}

export default new UISettingsStore();
